import {
  DndContext,
  DragOverlay,
  pointerWithin,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faArrowsUpDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import { ClubOnboardingSettingsDTO } from "@gymflow/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, DragAndDropIcon } from "../../atoms";
import { DotBadge } from "../../atoms/DotBadge";
import { onboardingSettingsList } from "./constants";

export function OnboardingSetting({
  id,
  children,
  moveToOtherColumn,
  disabled,
}: {
  id: string;
  children: React.ReactNode;
  moveToOtherColumn?: () => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform } =
    useDraggable({
      id,
      disabled,
    });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-gray-0 shadow-xs flex select-none flex-row gap-2 rounded-[10px] border border-gray-200 p-2"
    >
      <div
        {...listeners}
        {...attributes}
        ref={setActivatorNodeRef}
        className={cn("flex items-center justify-center", {
          "cursor-grab": !disabled,
        })}
      >
        {!disabled && (
          <DragAndDropIcon className="size-6" pathClassName="fill-gray-500" />
        )}
      </div>
      <div className="flex-1 truncate font-medium text-gray-950">
        {children}
      </div>
      {moveToOtherColumn && !disabled && (
        <Button
          className="p-1 px-2 lg:hidden"
          size="icon"
          onClick={moveToOtherColumn}
        >
          <FontAwesomeIcon icon={faArrowsUpDown} size="xs" />
        </Button>
      )}
    </div>
  );
}

export function OnboardingSettingLane({
  id,
  children,
}: {
  id: "REQUIRED" | "OPTIONAL";
  children: React.ReactNode[];
}) {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({
    id,
  });
  const [parent] = useAutoAnimate();
  const [childrenParent] = useAutoAnimate();
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "relative flex flex-1 flex-col gap-4 rounded-2xl border p-4 min-w-64",
        {
          "border-error-300": id === "REQUIRED",
          "bg-error-25": !isOver && id === "REQUIRED",
          "bg-error-50": isOver && id === "REQUIRED",
          "border-gray-300": id === "OPTIONAL",
          "bg-gray-25": !isOver && id === "OPTIONAL",
          "bg-gray-50": isOver && id === "OPTIONAL",
        },
      )}
    >
      <div className="flex flex-row items-center gap-2">
        <DotBadge
          intent={
            (
              {
                REQUIRED: "error",
                OPTIONAL: "gray",
              } as const
            )[id]
          }
          size="lg"
        >
          {
            {
              REQUIRED: t("page.settings.onboarding.required"),
              OPTIONAL: t("page.settings.onboarding.optional"),
            }[id]
          }
        </DotBadge>
        <div ref={parent}>
          <div
            key={children.length}
            className="flex flex-col gap-2 font-semibold text-gray-500"
          >
            {children.length}
          </div>
        </div>
      </div>
      <div ref={childrenParent} className="flex flex-col gap-2">
        {children}
      </div>
    </div>
  );
}
export const OnboardingRightMenu = ({
  localState,
  setLocalState,
}: {
  localState: ClubOnboardingSettingsDTO;
  setLocalState: (state: ClubOnboardingSettingsDTO) => void;
}) => {
  const { t } = useTranslation();
  const [parent] = useAutoAnimate();
  const containers = ["REQUIRED", "OPTIONAL"] as const;

  const [activeId, setActiveId] = useState<string>();

  const activeSetting = activeId
    ? onboardingSettingsList.find((setting) => setting.name === activeId)
    : null;

  return (
    <div
      ref={parent}
      className="flex flex-col justify-start gap-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm max-lg:h-[228rem] max-lg:w-full lg:h-full lg:w-2/3"
    >
      <div className="flex text-base font-medium text-gray-500">
        {t("page.settings.onboarding.description2")}
      </div>
      <div className="flex size-full gap-3 overflow-x-auto max-lg:flex-col lg:flex-row">
        <DndContext
          collisionDetection={pointerWithin}
          onDragStart={(event) => {
            setActiveId(event.active.id as string);
          }}
          onDragEnd={(event) => {
            const { active, over } = event;
            setActiveId(undefined);

            if (over && active.id !== over.id) {
              const newLocalState = {
                ...localState,
                [active.id]: over.id,
              };
              setLocalState(newLocalState);
            }
          }}
          onDragCancel={() => {
            setActiveId(undefined);
          }}
        >
          {containers.map((laneType) => (
            <OnboardingSettingLane key={laneType} id={laneType}>
              {onboardingSettingsList
                .filter((setting) => setting.name !== activeId)
                .filter((setting) => localState[setting.name] === laneType)
                .map((setting) => (
                  <OnboardingSetting
                    key={setting.name}
                    id={setting.name}
                    disabled={setting.disabled}
                    moveToOtherColumn={() => {
                      const currentValue = localState[setting.name];
                      const newLocalState = {
                        ...localState,
                        [setting.name]:
                          currentValue === "REQUIRED" ? "OPTIONAL" : "REQUIRED",
                      };
                      setLocalState(newLocalState);
                    }}
                  >
                    {setting.label}
                  </OnboardingSetting>
                ))}
            </OnboardingSettingLane>
          ))}
          <DragOverlay>
            {activeSetting ? (
              <OnboardingSetting
                id={activeSetting.name}
                disabled={activeSetting.disabled}
              >
                {activeSetting.label}
              </OnboardingSetting>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};
